import { ValidateLessonContentsPopUpContext } from "components/utils/Contexts";
import DotNavigationMap from "components/utils/DotNavigationMap";
import { animate, AnimatePresence, motion } from "framer-motion";
import { useContext, useEffect, useRef, useState } from "react";
import { FaArrowLeft, FaArrowRight, FaExclamation } from "react-icons/fa6";
import { ImLoop2 } from "react-icons/im";
import { MdCheck, MdClose, MdQuestionMark } from "react-icons/md";
import Markdown from "react-markdown";
import rehypeRaw from "rehype-raw";

interface ItemToValidateProps {
  text: string;
  info: {
    status: "loading" | "successful" | "error" | "warning";
    report?: any;
  };
  contentType: string;
  onDetailClick: (contentClicked: string) => void;
  onCloseErrorDetail: () => void;
}

const markdownComponents: any = {
  highlight: ({ children }: any) => {
    return <span style={{ color: "red", fontWeight: "bold" }}>{children}</span>;
  },
};

const ItemToValidate: React.FC<ItemToValidateProps> = ({
  text,
  info,
  contentType,
  onDetailClick,
  onCloseErrorDetail,
}) => {
  // States definition
  const [isOpen, setIsOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [detailButtonAnchorPos, setDetailButtonAnchorPos] = useState({
    x: 0,
    y: 0,
  });
  const [movement, setMovement] = useState(0);
  const [currentDetailButtonPos, setCurrentDetailButtonPos] = useState({
    x: 0,
    y: 0,
  });
  const [contentHeight, setContentHeight] = useState(0);

  // Reference for the detail button
  const detailButtonRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Get the context parameters
  const {
    detailPanelRef,
    detailToShow,
    detailPanelSizes,
    PANEL_MOVEMENT_ANIMATION_TIME,
    setDetailContentHeight,
    setDetailFinalPanelY,
  }: {
    detailPanelRef: React.RefObject<HTMLDivElement>;
    detailToShow: string;
    detailPanelSizes: { width: number; height: number };
    PANEL_MOVEMENT_ANIMATION_TIME: number;
    setDetailContentHeight: (h: number) => void;
    setDetailFinalPanelY: (y: number) => void;
  } = useContext(ValidateLessonContentsPopUpContext);

  /**
   * Observer routine for the height of the content
   * when being selected
   */
  useEffect(() => {
    if (!contentRef.current) return;
    const observer = new ResizeObserver((entries) => {
      setContentHeight(entries[0].contentRect.height);
    });
    observer.observe(contentRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (detailToShow === contentType) {
      setDetailContentHeight(contentHeight);
      setDetailFinalPanelY(finalPanelY);
    }
  }, [contentHeight, currentDetailButtonPos, detailToShow]);

  // Definition of the width and height of the moving panel
  // based on the movement index
  const panelWidth = movement * 381 + (1 - movement) * 20;
  const panelHeight = movement * (contentHeight + 40) + (1 - movement) * 20;
  const panelX = movement * 440 + (1 - movement) * 405;
  // Triangle center Y = its translated Y + half of size-10 (20px)
  const triangleCenterY = currentDetailButtonPos.y + 34;
  // Ideal panel top when centered on the triangle; clamp so it never overflows the top
  const finalPanelY = Math.max(24, triangleCenterY - (contentHeight + 40) / 2);
  const panelY =
    movement * finalPanelY +
    (1 - movement) * (detailButtonAnchorPos.y + 24 - panelHeight / 2);

  /**
   * Hook that controls the opening and closing of the
   * error detail panel
   */
  useEffect(() => {
    // If the current component is selected,
    // then animate it's opening
    if (detailToShow === contentType) {
      // Animate the value of movement from 0 to 1
      const controls = animate(0, 1, {
        duration: PANEL_MOVEMENT_ANIMATION_TIME,
        ease: "easeOut",
        onUpdate: (latest) => setMovement(latest),
      });

      return () => controls.stop();
    }
    // Make sure that the detail to show is different,
    // but is defined. When it's null it means that it
    // needs to be closed, which is controlled by the
    // `handleCloseErrorDetail` function.
    // An extra state `isOpen` is used to prevent
    // triggering closed panels
    else if (detailToShow !== contentType && !!detailToShow && isOpen) {
      // Remove the open flag
      setIsOpen(false);

      // Animate the value of movement from 0 to 1
      const controls = animate(1, 0, {
        duration: PANEL_MOVEMENT_ANIMATION_TIME,
        ease: "easeOut",
        onUpdate: (latest) => setMovement(latest),
      });

      return () => controls.stop();
    }
  }, [detailToShow]);

  /**
   * Hook used to update the current position of the original
   * error detail button (when we are opening the panel, this
   * button is moving around the pop up window when the detail
   * panel is opened)
   */
  useEffect(() => {
    // Check if the container has reference
    if (detailButtonRef.current && detailPanelRef.current) {
      // Get the current button position
      const { x: xButton, y: yButton } =
        detailButtonRef.current?.getBoundingClientRect();
      const { x: xContainer, y: yContainer } =
        detailPanelRef.current?.getBoundingClientRect();

      // Set the position of the button as the panel position
      setCurrentDetailButtonPos({
        x: xButton - xContainer,
        y: yButton - yContainer,
      });
    }
  }, [detailPanelSizes]);

  /**
   * Function executed when clicking the error detail button
   */
  const handleErrorDetailClick = () => {
    // Execute the external onDetailClick
    onDetailClick(contentType);

    // And flag this as open to make sure
    // it needs to be closed on `detailToShow`
    // changes
    setIsOpen(true);
  };

  /**
   * Function executed when clicking the cross to close the
   * error detail panel
   */
  const handleCloseErrorDetail = () => {
    // Execute the external onCloseErrorDetail
    onCloseErrorDetail();

    // Animate the value of movement from 1 to 0
    const controls = animate(1, 0, {
      duration: PANEL_MOVEMENT_ANIMATION_TIME,
      ease: "easeOut",
      onUpdate: (latest) => setMovement(latest),
    });

    return () => controls.stop();
  };

  /**
   * Function used to update the detail button anchor position
   */
  const updateDetailButtonAnchorPos = () => {
    // Check the detail button has reference
    if (detailButtonRef.current && detailPanelRef.current) {
      // Get the current button position
      const { x: xButton, y: yButton } =
        detailButtonRef.current?.getBoundingClientRect();
      const { x: xContainer, y: yContainer } =
        detailPanelRef.current?.getBoundingClientRect();

      // Set the position of the button as the panel position
      setDetailButtonAnchorPos({
        x: xButton - xContainer,
        y: yButton - yContainer,
      });
    }
  };

  /**
   * Function used to go to the previous page when the item
   * contains more than one page for report
   */
  const handlePressPrevPage = () => {
    setCurrentPage((currentPage) => Math.max(0, currentPage - 1));
  };

  /**
   * Function used to go to the next page when the item
   * contains more than one page for report
   */
  const handlePressNextPage = () => {
    // Get the total number of pages
    if (info && info.report && info.report.length) {
      setCurrentPage((currentPage) =>
        Math.min(info.report.length - 1, currentPage + 1),
      );
    }
  };

  return (
    <>
      <div className="self-stretch inline-flex gap-3 relative">
        <div className="relative size-5 overflow-hidden">
          <AnimatePresence>
            {info.status === "successful" ? (
              <motion.div
                key="successful-status"
                initial={{ y: "100%" }}
                animate={{ y: 0 }}
                exit={{ y: "-100%" }}
                className="inset-0 absolute rounded-full size-5 bg-success-500 inline-flex items-center justify-center"
              >
                <MdCheck className="size-3 fill-white" />
              </motion.div>
            ) : info.status === "error" ? (
              <motion.div
                key="error-state"
                initial={{ y: "100%" }}
                animate={{ y: 0 }}
                exit={{ y: "-100%" }}
                className="inset-0 absolute rounded-full size-5 bg-error-500 inline-flex items-center justify-center"
              >
                <FaExclamation className="size-3 fill-white" />
              </motion.div>
            ) : info.status === "warning" ? (
              <motion.div
                key="warning-state"
                initial={{ y: "100%" }}
                animate={{ y: 0 }}
                exit={{ y: "-100%" }}
                className="inset-0 absolute rounded-full size-5 bg-warning-500 inline-flex items-center justify-center"
              >
                <FaExclamation className="size-3 fill-white" />
              </motion.div>
            ) : (
              <motion.div
                key="loading-state"
                initial={{ y: "100%" }}
                animate={{ y: 0 }}
                exit={{ y: "-100%" }}
                className="inset-0 absolute rounded-full size-5 bg-skb-700 inline-flex items-center justify-center"
              >
                <motion.div
                  key="rotating-loop"
                  animate={{ rotate: [0, 90, 180, 270, 360] }}
                  transition={{
                    repeat: Infinity,
                    duration: 4,
                    type: "easeOut",
                    times: [
                      0,
                      0.15, // stay at 0
                      0.25,
                      0.4, // stay at 90
                      0.5,
                      0.65, // stay at 180
                      0.75,
                      0.9, // stay at 270
                      1, // back to 360 (same as 0)
                    ],
                  }}
                  className="size-2.5"
                >
                  <ImLoop2 className="size-full fill-white" />
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <div className="justify-start text-neutral-600 text-base font-normal font-sans leading-tight">
          {text}
        </div>
        <AnimatePresence>
          {["error", "warning"].includes(info.status) && (
            <motion.div
              ref={detailButtonRef}
              key="question-mark"
              initial={{ x: "100%", opacity: 0 }}
              animate={{ x: "0", opacity: 1 }}
              exit={{ x: "100%", opacity: 0 }}
              style={{ visibility: movement > 0 ? "hidden" : "visible" }}
              onClick={handleErrorDetailClick}
              className="absolute right-0 z-[50]"
              onAnimationComplete={updateDetailButtonAnchorPos}
            >
              <div
                className={`rounded-full size-5 ${
                  info.status === "error"
                    ? "bg-error-50 hover:bg-error-100"
                    : "bg-warning-50 hover:bg-warning-100"
                } ease-in-out duration-75 inline-flex items-center justify-center cursor-pointer`}
              >
                <MdQuestionMark
                  className={`size-4 ${
                    info.status === "error"
                      ? "fill-error-500"
                      : "fill-warning-500"
                  }`}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      {/* Mobile button */}
      {movement > 0 && (
        <div
          key={`mobile-button-${contentType}`}
          style={{
            opacity: 1 - movement,
            transform: `translate(${
              movement * (451 + 330) + (1 - movement) * 406
            }px, ${
              movement * 44 + (1 - movement) * (detailButtonAnchorPos.y + 24)
            }px)`,
          }}
          onClick={handleCloseErrorDetail}
          className={`absolute inset-0 rounded-full size-5 ${
            info.status === "error" ? "bg-error-50" : "bg-warning-50"
          } inline-flex items-center justify-center cursor-pointer z-20`}
        >
          <MdQuestionMark
            className={`size-4 ${
              info.status === "error" ? "fill-error-500" : "fill-warning-500"
            }`}
          />
        </div>
      )}
      {/* Mobile detail panel */}
      <div
        key="error-details-panel"
        style={{
          width: panelWidth,
          height: panelHeight,
          opacity: movement,
          visibility: movement > 0 ? "visible" : "hidden",
          transform: `translate(${panelX}px, ${panelY}px)`,
          transformOrigin: "center",
        }}
        className="absolute inset-0 max-h-[80vh] inline-flex items-center overflow-hidden z-30 origin-center"
      >
        <div
          ref={contentRef}
          className="w-full bg-neutral-50 max-h-[75vh] rounded-3xl inline-flex flex-col p-5 gap-6 justify-start items-start"
        >
          {/* Header */}
          <div className="inline-flex gap-1">
            <div className="justify-start text-base text-neutral-700 font-normal font-sans leading-normal">
              We've identified content that doesn't meet our platform
              guidelines. Please review and revise the highlighted sections
              below, then try to save the lesson again.
            </div>
            <div
              className="inline-flex size-5 p-1 items-center justify-center rounded-full bg-neutral-100 hover:bg-neutral-200 ease-in-out duration-150 cursor-pointer"
              onClick={handleCloseErrorDetail}
            >
              <MdClose className="fill-neutral-500 size-3" />
            </div>
          </div>
          {/* Content */}
          {info && info.report && info.report.length > 0 && (
            <div
              className={`w-full p-4 inline-flex flex-col items-start${
                movement === 1
                  ? " overflow-y-auto custom-scroll [--scroll-color:theme(colors.neutral.50)]"
                  : ""
              } gap-4 self-start rounded-2xl ${
                info.status === "error" ? "bg-error-50" : "bg-warning-50"
              }`}
            >
              <div className="self-stretch justify-start text-text-headings-body-regular text-base font-normal font-sans leading-normal text-neutral-700">
                📍 Issue found in: {text}
              </div>
              <div
                className="self-stretch p-4 bg-white rounded-xl inline-flex justify-center items-center gap-2"
                style={{
                  boxShadow: `-4px 0 0 0 ${
                    info.status === "error" ? "#FF335B" : "#F19B09"
                  }`,
                }}
              >
                <div className="w-full justify-start break-words text-neutral-700">
                  <Markdown
                    rehypePlugins={[rehypeRaw]}
                    components={markdownComponents}
                  >
                    {info.report[currentPage]}
                  </Markdown>
                </div>
              </div>
            </div>
          )}
          {/* Navigation panel (in case of more than one report) */}
          {info && info.report && info.report.length > 1 && (
            <div className="self-stretch inline-flex justify-between items-center gap-6">
              {/* Prev button */}
              <div
                className="size-5 p-1 bg-neutral-100 hover:bg-neutral-200 active:bg-neutral-300 ease-in-out duration-150 rounded-full inline-flex justify-center items-center cursor-pointer"
                style={{ visibility: currentPage === 0 ? "hidden" : "visible" }}
                onClick={handlePressPrevPage}
              >
                <FaArrowLeft className="size-3 fill-neutral-500" />
              </div>
              {/* Dot map showing the navigation throuh pages */}
              <DotNavigationMap
                numItems={info.report?.length || 0}
                currentItem={currentPage}
                maxNumDotsToShow={5}
              />
              {/* Next button */}
              <div
                className="size-5 p-1 bg-neutral-100 hover:bg-neutral-200 active:bg-neutral-300 ease-in-out duration-150 rounded-full inline-flex justify-center items-center cursor-pointer"
                style={{ visibility: currentPage === 4 ? "hidden" : "visible" }}
                onClick={handlePressNextPage}
              >
                <FaArrowRight className="size-3 fill-neutral-500" />
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Show this triangle to point the current content to be checked after the panel
      has been fully opened */}
      {movement === 1 && (
        <motion.div
          key="triangle"
          initial={{
            x: 445,
            y: currentDetailButtonPos.y + 14,
            rotate: 45,
          }}
          animate={{
            x: 425,
            y: currentDetailButtonPos.y + 14,
            rotate: 45,
          }}
          exit={{
            x: 445,
            y: currentDetailButtonPos.y + 14,
            rotate: 45,
          }}
          className="absolute inset-0 size-10 bg-neutral-50 rounded-md overflow-hidden z-20 origin-center"
        />
      )}
    </>
  );
};

export default ItemToValidate;
